import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Box, Typography, ButtonBase } from '@mui/material';
import { EventItem } from '@scorenco/components';
import { Types } from '@scorenco/core';
import { RouteLink } from '@scorenco/next/src/services/NextRouterService/RouteLink';
import { useRouter } from 'next/router';

import {
  GetFilteredTeamsDocument,
  GetTeamsDocument,
} from './_internal/Widget.query.generated';

type EventsListWidgetProps = {
  clubId?: number;
  teams?: Types.Widgets_Widget_Has_Team[];
  sort?: string;
  daySwitch?: string;
};

export const EventsListWidget = ({
  teams,
  sort,
  clubId,
}: EventsListWidgetProps) => {
  const router = useRouter();
  const now = useMemo(() => new Date(), []);

  const getAllTeams = useQuery(GetTeamsDocument, {
    variables: {
      clubId,
      now,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !clubId || teams?.length > 0,
    context: {
      role: 'anonymous',
    },
  });

  const getFilteredTeams = useQuery(GetFilteredTeamsDocument, {
    variables: {
      clubId,
      now,
      teamsIds: teams?.map((team) => team.team_id),
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !clubId || teams?.length === 0,
    context: {
      role: 'anonymous',
    },
  });

  const widgetTeams =
    teams?.length === 0
      ? getAllTeams.data?.teams_team_detail_by_club_id
      : getFilteredTeams.data?.teams_team_detail_by_club_id;

  return (
    <Box sx={{ width: '100%', marginBottom: '1.5rem' }}>
      {widgetTeams?.map((team, idx) => {
        const lastEvent = team?.last_events?.[0]?.event_detail?.[0];
        const nextEvent = team?.next_events?.[0]?.event_detail?.[0];
        const differentCompetition =
          lastEvent?.competition?.id !== nextEvent?.competition?.id;

        return (
          <Box key={idx}>
            {lastEvent && (
              <Typography
                variant="h5"
                sx={{
                  backgroundColor: 'background.paper2',
                  textAlign: 'center',
                  padding: '0.5rem',
                  border: '1px solid #d2e0ef',
                }}
              >
                {differentCompetition
                  ? lastEvent?.competition?.name
                  : team?.team_in_season?.last_competition_with_ranking?.[0]
                      ?.name}
              </Typography>
            )}
            {lastEvent && sort !== 'next' && (
              <RouteLink
                href={`${process.env.NEXT_PUBLIC_WEB_URL}${lastEvent?.url}`}
              >
                <ButtonBase
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: (theme) => theme.vars.palette.divider,
                    borderLeft: 'none',
                    borderRight: 'none',
                    width: '100%',
                    '&:hover': {
                      background: (theme) =>
                        theme.vars.palette.background.paper2,
                    },
                  }}
                >
                  <EventItem
                    component="a"
                    event={lastEvent}
                    clubId={clubId}
                    showShortName={true}
                    showPlace={false}
                    showCompetition={false}
                    showDate={true}
                  />
                </ButtonBase>
              </RouteLink>
            )}
            {differentCompetition && nextEvent?.competition?.name && (
              <Typography
                variant="h5"
                sx={{
                  backgroundColor: 'background.paper2',
                  textAlign: 'center',
                  padding: '0.5rem',
                  border: '1px solid #d2e0ef',
                }}
              >
                {nextEvent?.competition?.name}
              </Typography>
            )}
            {nextEvent && sort !== 'last' && (
              <RouteLink
                href={`${process.env.NEXT_PUBLIC_WEB_URL}${nextEvent?.url}`}
              >
                <ButtonBase
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: (theme) => theme.vars.palette.divider,
                    borderLeft: 'none',
                    borderRight: 'none',
                    width: '100%',
                    '&:hover': {
                      background: (theme) =>
                        theme.vars.palette.background.paper2,
                    },
                  }}
                >
                  <EventItem
                    event={nextEvent}
                    clubId={clubId}
                    showShortName={true}
                    showPlace={false}
                    showCompetition={false}
                    showDate={true}
                  />
                </ButtonBase>
              </RouteLink>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
